import { useScrollReveal, getRevealClasses } from "@/hooks/useScrollReveal";
import useSEO from '@/hooks/useSEO';
import {
  scenarioData,
  xmAccountTypes,
  icmarketsAccountTypes,
  xmHonestTake,
  icmarketsHonestTake,
  accountMatchups,
  techData,
  xmPerks,
  icmarketsPerks,
  blackSwanData,
} from "@/mocks/icmarketsCompareData";
import BrokerQuiz from "./components/BrokerQuiz";
import { brokers } from "@/data/brokers";

/* ------------------------------------------------------------------ */
/*  Shared sub-components                                             */
/* ------------------------------------------------------------------ */

function SectionHeader({ badge, badgeIcon, title, subtitle, badgeColor = "#10B981" }: {
  badge: string; badgeIcon: string; title: string; subtitle?: string; badgeColor?: string;
}) {
  return (
    <div className="text-center mb-12 md:mb-16">
      <div
        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-5"
        style={{ borderColor: `${badgeColor}/30`, backgroundColor: `${badgeColor}/8` }}
      >
        <i className={`${badgeIcon}`} style={{ color: badgeColor }}></i>
        <span className="text-sm text-[#F8FAFC] font-medium">{badge}</span>
      </div>
      <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">{title}</h2>
      {subtitle && <p className="text-base text-[#94A3B8] max-w-2xl mx-auto">{subtitle}</p>}
    </div>
  );
}

function WinnerBadge({ winner }: { winner: "xm" | "icmarkets" | "tie" }) {
  if (winner === "tie") return null;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
      winner === "xm" ? "bg-[#7C3AED]/15 text-[#8B5CF6]" : "bg-[#10B981]/15 text-[#10B981]"
    }`}>
      <i className="ri-trophy-line text-xs"></i>
      {winner === "xm" ? "XM Wins" : "IC Markets Wins"}
    </span>
  );
}

function ScoreBar({ score, color }: { score: number; color: string }) {
  return (
    <div className="flex-1 h-2 bg-[#2A3345] rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-1000 ease-out"
        style={{ width: `${score * 10}%`, backgroundColor: color }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 1: Hero — The 1-Second Verdict                             */
/* ------------------------------------------------------------------ */

function HeroVerdict() {
  return (
    <section className="relative min-h-screen flex items-center pt-20 pb-16 overflow-hidden">
      {/* Split Background */}
      <div className="absolute inset-0 flex">
        <div className="w-1/2 h-full bg-gradient-to-br from-[#120726] via-[#1e0a3c]/90 to-[#111827]" />
        <div className="w-1/2 h-full bg-gradient-to-bl from-[#051C12] via-[#064E3B]/90 to-[#111827]" />
      </div>
      <div className="absolute inset-0 bg-[#111827]/10 pointer-events-none" />
      <div className="absolute left-1/2 top-20 bottom-20 w-px bg-gradient-to-b from-transparent via-[#F8FAFC]/30 to-transparent hidden md:block" />

      <div className="relative w-full max-w-7xl mx-auto px-4 md:px-6">
        {/* VS Badge */}
        <div className="flex justify-center mb-8 md:mb-12">
          <div className="relative z-10 inline-flex items-center gap-3 px-6 py-2.5 rounded-full bg-[#1F2937] border border-[#374151] shadow-2xl shadow-black/30">
            <span className="text-sm font-bold text-[#8B5CF6]">XM</span>
            <span className="w-10 h-10 rounded-full bg-gradient-to-r from-[#7C3AED] to-[#10B981] flex items-center justify-center text-white font-black text-sm shadow-lg">VS</span>
            <span className="text-sm font-bold text-[#10B981]">IC Markets</span>
          </div>
        </div>

        <h1 className="text-center text-4xl md:text-6xl lg:text-7xl font-black text-[#F8FAFC] mb-4 tracking-tight">
          The 1-Second Verdict
        </h1>
        <p className="text-center text-lg md:text-xl text-[#94A3B8] mb-14 max-w-2xl mx-auto">
          XM's bonus empire vs IC Markets' raw ECN speed. The accessibility champion meets the pure-play ECN king — who wins for your strategy?
        </p>

        {/* Verdict Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 mb-10">
          {/* XM */}
          <div className="relative bg-[#120726]/80 backdrop-blur-sm border border-[#7C3AED]/30 rounded-2xl p-8 md:p-10 hover:border-[#8B5CF6]/50 transition-all duration-300 group">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#7C3AED] to-[#8B5CF6] rounded-t-2xl" />
            <div className="flex items-center gap-3 mb-5">
              <img src="/images/xm.jpg" alt="XM" className="w-12 h-12 rounded-xl object-cover border border-[#7C3AED]/30" />
              <div>
                <h3 className="text-xl font-bold text-[#F8FAFC]">XM Trading</h3>
                <p className="text-xs text-[#64748B]">Est. 2009 · ASIC/CySEC Regulated</p>
              </div>
            </div>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
              Best for traders who value{" "}
              <span className="text-[#8B5CF6] font-semibold">bonus perks, low entry barriers, and rock-solid execution during volatile events</span>.
              Start with $5 and trade with the broker's money through aggressive welcome bonuses.
            </p>
            <a href={brokers.xm.href} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 w-full justify-center px-6 py-3.5 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-sm font-bold rounded-lg transition-all hover:shadow-lg hover:shadow-[#7C3AED]/30 whitespace-nowrap">
              Claim $30 No-Deposit Bonus
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>

          {/* IC Markets */}
          <div className="relative bg-[#051C12]/80 backdrop-blur-sm border border-[#10B981]/30 rounded-2xl p-8 md:p-10 hover:border-[#34D399]/50 transition-all duration-300 group">
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#10B981] to-[#34D399] rounded-t-2xl" />
            <div className="flex items-center gap-3 mb-5">
              <img src="/images/ic.jpg" alt="IC Markets" className="w-12 h-12 rounded-xl object-cover border border-[#10B981]/30" />
              <div>
                <h3 className="text-xl font-bold text-[#F8FAFC]">IC Markets</h3>
                <p className="text-xs text-[#64748B]">Est. 2007 · ASIC/CySEC/FSA Regulated</p>
              </div>
            </div>
            <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
              Built for precision traders demanding{" "}
              <span className="text-[#10B981] font-semibold">raw ECN spreads from 0.0 pips, sub-40ms execution, and the largest instrument range</span>.
              No gimmicks — just pure interbank pricing with institutional-grade infrastructure.
            </p>
            <a href={brokers.icmarkets.href} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 w-full justify-center px-6 py-3.5 bg-[#10B981] hover:bg-[#059669] text-white text-sm font-bold rounded-lg transition-all hover:shadow-lg hover:shadow-[#10B981]/30 whitespace-nowrap">
              Try cTrader Raw Account
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>
        </div>

        <div className="flex justify-center">
          <div className="animate-bounce text-[#64748B]">
            <i className="ri-arrow-down-line text-2xl"></i>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 2: 4 Real Trading Scenarios Showdown                       */
/* ------------------------------------------------------------------ */

function ScenarioShowdown() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Live Fire Test" badgeIcon="ri-sword-line" badgeColor="#F59E0B"
          title='Which Broker Makes You More Money?'
          subtitle="We put both platforms through 4 real-world trading situations — scalping, small accounts, swing trading, and news events — so you can see where your strategy actually wins."
        />

        <div className="space-y-6">
          {scenarioData.map((item) => (
            <div key={item.id} className="bg-[#111827] border border-[#1F2937] rounded-2xl overflow-hidden hover:border-[#374151] transition-all duration-300">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-2 p-6 bg-[#1A2235] flex flex-col items-center justify-center text-center gap-2 border-b lg:border-b-0 lg:border-r border-[#2A3345]">
                  <div className="w-12 h-12 rounded-xl bg-[#2A3345] flex items-center justify-center">
                    <i className={`${item.icon} text-xl text-[#F59E0B]`}></i>
                  </div>
                  <div className="text-xs text-[#64748B] uppercase tracking-wider">{item.scenarioTag}</div>
                  <div className="text-sm font-bold text-[#F8FAFC]">{item.scenario}</div>
                </div>
                <div className={`lg:col-span-4 p-6 border-b lg:border-b-0 lg:border-r border-[#2A3345] ${item.winner === "xm" ? "bg-[#7C3AED]/3" : ""}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <img src="/images/xm.jpg" alt="XM" className="w-6 h-6 rounded object-cover" />
                    {item.winner === "xm" && <WinnerBadge winner="xm" />}
                  </div>
                  <div className="text-base font-bold text-[#F8FAFC] mb-2">{item.xmValue}</div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{item.xmDetail}</p>
                </div>
                <div className={`lg:col-span-4 p-6 border-b lg:border-b-0 lg:border-r border-[#2A3345] ${item.winner === "icmarkets" ? "bg-[#10B981]/3" : ""}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <img src="/images/ic.jpg" alt="IC Markets" className="w-6 h-6 rounded object-cover" />
                    {item.winner === "icmarkets" && <WinnerBadge winner="icmarkets" />}
                  </div>
                  <div className="text-base font-bold text-[#F8FAFC] mb-2">{item.icmarketsValue}</div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{item.icmarketsDetail}</p>
                </div>
                <div className="lg:col-span-2 p-6 bg-[#1A2235] flex items-center">
                  <p className="text-xs text-[#94A3B8] leading-relaxed italic">{item.reason}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 3: Complete Account Types Comparison                        */
/* ------------------------------------------------------------------ */

function AccountTypesComparison() {
  const { ref, isVisible } = useScrollReveal(0.1);

  const statRows = [
    { label: "Spread", xmKey: "spread" as const, icKey: "spread" as const },
    { label: "Commission", xmKey: "commission" as const, icKey: "commission" as const },
    { label: "Min Deposit", xmKey: "minDeposit" as const, icKey: "minDeposit" as const },
    { label: "Max Leverage", xmKey: "leverage" as const, icKey: "leverage" as const },
  ];

  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Account Inventory" badgeIcon="ri-bank-card-line" badgeColor="#F59E0B"
          title='Every Account Type — Side by Side'
          subtitle="XM offers 4 account types: Micro, Standard, Ultra Low, and Shares. IC Markets runs 3 tiers: Standard, Raw Spread MT, and cTrader Raw. See exactly what you get at every level — no cherry-picking, every account shown."
        />

        {/* Summary row: count badges */}
        <div className="flex items-center justify-center gap-16 mb-12">
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#7C3AED]/10 border border-[#7C3AED]/20 flex items-center justify-center mx-auto mb-3">
              <span className="text-[#8B5CF6] font-black text-xl">4</span>
            </div>
            <span className="text-xs font-bold text-[#8B5CF6] uppercase tracking-wider">XM Accounts</span>
          </div>
          <div className="w-px h-12 bg-[#2A3345]" />
          <div className="text-center">
            <div className="w-14 h-14 rounded-2xl bg-[#10B981]/10 border border-[#10B981]/20 flex items-center justify-center mx-auto mb-3">
              <span className="text-[#10B981] font-black text-xl">3</span>
            </div>
            <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">IC Markets Accounts</span>
          </div>
        </div>

        {/* Master grid: 3 paired rows + 1 XM-only row for Shares */}
        <div className="space-y-6">
          {[0, 1, 2].map((idx) => {
            const xm = xmAccountTypes[idx];
            const ic = icmarketsAccountTypes[idx];
            return (
              <div key={idx} className="bg-[#0B0F19] border border-[#1F2937] rounded-2xl overflow-hidden hover:border-[#374151] transition-all duration-300">
                {/* Row header: tier label */}
                <div className="grid grid-cols-1 md:grid-cols-2 bg-[#1A2235] border-b border-[#2A3345]">
                  <div className="px-6 py-4 flex items-center gap-3 border-b md:border-b-0 md:border-r border-[#2A3345]">
                    <img src="/images/xm.jpg" alt="XM" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-[#F8FAFC] truncate">{xm.name}</h3>
                    </div>
                    <span className="ml-auto text-[10px] font-semibold text-[#8B5CF6] bg-[#7C3AED]/10 px-2 py-0.5 rounded-full whitespace-nowrap">{xm.highlight}</span>
                  </div>
                  <div className="px-6 py-4 flex items-center gap-3">
                    <img src="/images/ic.jpg" alt="" className="w-8 h-8 rounded-lg object-cover shrink-0" />
                    <div className="min-w-0">
                      <h3 className="text-sm font-bold text-[#F8FAFC] truncate">{ic.name}</h3>
                    </div>
                    <span className="ml-auto text-[10px] font-semibold text-[#10B981] bg-[#10B981]/10 px-2 py-0.5 rounded-full whitespace-nowrap">{ic.highlight}</span>
                  </div>
                </div>

                {/* Stats row */}
                <div className="grid grid-cols-1 md:grid-cols-2 border-b border-[#1F2937]">
                  {/* XM stats */}
                  <div className="p-6 md:border-r border-[#1F2937]">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      {statRows.map((row) => (
                        <div key={row.label} className="bg-[#111827] rounded-lg p-3">
                          <span className="block text-[10px] text-[#64748B] uppercase tracking-wider mb-0.5">{row.label}</span>
                          <span className="block text-xs font-bold text-[#F8FAFC] leading-tight">{xm[row.xmKey]}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#94A3B8] leading-relaxed mb-3">{xm.note}</p>
                    <div className="flex items-center gap-2 text-[10px] text-[#8B5CF6] font-semibold">
                      <i className="ri-user-line text-[11px]"></i>
                      {xm.bestFor}
                    </div>
                  </div>
                  {/* IC Markets stats */}
                  <div className="p-6">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                      {statRows.map((row) => (
                        <div key={row.label} className="bg-[#111827] rounded-lg p-3">
                          <span className="block text-[10px] text-[#64748B] uppercase tracking-wider mb-0.5">{row.label}</span>
                          <span className="block text-xs font-bold text-[#F8FAFC] leading-tight">{ic[row.icKey]}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#94A3B8] leading-relaxed mb-3">{ic.note}</p>
                    <div className="flex items-center gap-2 text-[10px] text-[#10B981] font-semibold">
                      <i className="ri-user-line text-[11px]"></i>
                      {ic.bestFor}
                    </div>
                  </div>
                </div>

                {/* Features row */}
                <div className="grid grid-cols-1 md:grid-cols-2">
                  <div className="p-6 md:border-r border-[#1F2937]">
                    <span className="block text-[10px] text-[#64748B] uppercase tracking-wider mb-3">Key Features</span>
                    <ul className="space-y-2">
                      {xm.features.map((feat, fi) => (
                        <li key={fi} className="flex items-start gap-2.5">
                          <i className="ri-check-line text-[#8B5CF6] text-xs mt-0.5 shrink-0"></i>
                          <span className="text-xs text-[#94A3B8] leading-relaxed">{feat}</span>
                        </li>
                      ))}
                    </ul>
                    <a href={brokers.xm.href} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 border border-[#7C3AED]/30 hover:border-[#8B5CF6]/50 text-[#8B5CF6] text-xs font-semibold rounded-lg transition-all whitespace-nowrap">
                      Open Account Now
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                  <div className="p-6">
                    <span className="block text-[10px] text-[#64748B] uppercase tracking-wider mb-3">Key Features</span>
                    <ul className="space-y-2">
                      {ic.features.map((feat, fi) => (
                        <li key={fi} className="flex items-start gap-2.5">
                          <i className="ri-check-line text-[#10B981] text-xs mt-0.5 shrink-0"></i>
                          <span className="text-xs text-[#94A3B8] leading-relaxed">{feat}</span>
                        </li>
                      ))}
                    </ul>
                    <a href={brokers.icmarkets.href} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-[#10B981]/10 hover:bg-[#10B981]/20 border border-[#10B981]/30 hover:border-[#34D399]/50 text-[#34D399] text-xs font-semibold rounded-lg transition-all whitespace-nowrap">
                      Open Account Now
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Row 4: XM Shares — IC Markets has no equivalent */}
          <div className="bg-[#0B0F19] border border-[#7C3AED]/20 rounded-2xl overflow-hidden hover:border-[#7C3AED]/40 transition-all duration-300">
            <div className="bg-[#1A2235] border-b border-[#2A3345] px-6 py-4 flex items-center gap-3">
              <img src="/images/xm.jpg" alt="XM" className="w-8 h-8 rounded-lg object-cover shrink-0" />
              <h3 className="text-sm font-bold text-[#F8FAFC]">{xmAccountTypes[3].name}</h3>
              <span className="ml-auto text-[10px] font-semibold text-[#8B5CF6] bg-[#7C3AED]/10 px-2 py-0.5 rounded-full whitespace-nowrap">{xmAccountTypes[3].highlight}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-6">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                  {statRows.map((row) => (
                    <div key={row.label} className="bg-[#111827] rounded-lg p-3">
                      <span className="block text-[10px] text-[#64748B] uppercase tracking-wider mb-0.5">{row.label}</span>
                      <span className="block text-xs font-bold text-[#F8FAFC] leading-tight">{xmAccountTypes[3][row.xmKey]}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#94A3B8] leading-relaxed mb-3">{xmAccountTypes[3].note}</p>
                <div className="flex items-center gap-2 text-[10px] text-[#8B5CF6] font-semibold">
                  <i className="ri-user-line text-[11px]"></i>
                  {xmAccountTypes[3].bestFor}
                </div>
              </div>
              <div className="p-6 bg-[#111827] border-l border-[#1F2937] flex flex-col items-center justify-center text-center">
                <div className="w-14 h-14 rounded-full bg-[#10B981]/5 flex items-center justify-center mb-3">
                  <i className="ri-forbid-line text-2xl text-[#64748B]"></i>
                </div>
                <span className="text-sm font-bold text-[#64748B] mb-1">No Equivalent IC Markets Account</span>
                <p className="text-xs text-[#64748B] leading-relaxed max-w-xs">IC Markets only offers CFD trading and does not support physical stock ownership. To trade 1,200+ global stocks with exchange pricing and dividend rights, XM Shares is a unique advantage.</p>
              </div>
            </div>
            <div className="p-6 border-t border-[#1F2937]">
              <span className="block text-[10px] text-[#64748B] uppercase tracking-wider mb-3">Key Features</span>
              <ul className="space-y-2">
                {xmAccountTypes[3].features.map((feat, fi) => (
                  <li key={fi} className="flex items-start gap-2.5">
                    <i className="ri-check-line text-[#8B5CF6] text-xs mt-0.5 shrink-0"></i>
                    <span className="text-xs text-[#94A3B8] leading-relaxed">{feat}</span>
                  </li>
                ))}
              </ul>
              <a href={brokers.xm.href} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 w-full mt-4 px-4 py-2.5 bg-[#7C3AED]/10 hover:bg-[#7C3AED]/20 border border-[#7C3AED]/30 hover:border-[#8B5CF6]/50 text-[#8B5CF6] text-xs font-semibold rounded-lg transition-all whitespace-nowrap">
                Open Account Now
                <i className="ri-arrow-right-line"></i>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom insight */}
        <div className="mt-10 bg-gradient-to-r from-[#7C3AED]/5 via-[#1A2235] to-[#10B981]/5 border border-[#2A3345] rounded-xl p-6 md:p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-[#F59E0B]/10 flex items-center justify-center mx-auto mb-4">
            <i className="ri-lightbulb-line text-[#F59E0B] text-xl"></i>
          </div>
          <h3 className="text-lg font-bold text-[#F8FAFC] mb-2">How to Pick the Right Account in 30 Seconds</h3>
          <div className="max-w-2xl mx-auto">
            <p className="text-sm text-[#94A3B8] leading-relaxed">
              <span className="text-[#8B5CF6] font-semibold">Under $200 budget?</span> XM Micro or Standard — $5 entry with zero commissions. IC Markets isn't even in the conversation at this price point.
              <span className="text-[#10B981] font-semibold"> Trading 10+ lots daily?</span> IC Markets cTrader Raw — 0.0 pips + $3/side commission is near unbeatable with sub-40ms execution.
              <span className="text-[#F59E0B] font-semibold"> cTrader is your platform?</span> IC Markets cTrader Raw — XM doesn't even offer cTrader. The lowest commission rate in IC's lineup at $3/side.
              <span className="text-[#8B5CF6] font-semibold"> Just want stocks?</span> XM Shares — 1,200+ global stocks with exchange pricing and dividend rights. IC Markets only offers stock CFDs, not physical ownership.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 4: Honest Downsides                                        */
/* ------------------------------------------------------------------ */

function HonestDownsides() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="The Real Deal" badgeIcon="ri-speak-line" badgeColor="#10B981"
          title='What Nobody Tells You About These Brokers'
          subtitle="Every broker has strengths they brag about and weaknesses they hide. We lay out both sides honestly — because trust comes from transparency, not sugarcoating."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* XM */}
          <div className="bg-[#111827] border border-[#7C3AED]/20 rounded-2xl overflow-hidden">
            <div className="bg-[#7C3AED]/10 px-6 py-4 border-b border-[#7C3AED]/10 flex items-center gap-3">
              <img src="/images/xm.jpg" alt="XM" className="w-8 h-8 rounded-lg object-cover" />
              <h3 className="text-lg font-bold text-[#F8FAFC]">XM: The Good & The Bad</h3>
            </div>
            <div className="p-6">
              {/* Pros */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 rounded-full bg-[#10B981]/15 flex items-center justify-center">
                    <i className="ri-check-line text-[11px] text-[#10B981]"></i>
                  </div>
                  <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">Where XM Shines</span>
                </div>
                <ul className="space-y-3">
                  {xmHonestTake.pros.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[10px] text-[#10B981]`}></i>
                      </div>
                      <p className="text-sm text-[#94A3B8] leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Cons */}
              <div className="pt-5 border-t border-[#1F2937]">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 rounded-full bg-[#EF4444]/10 flex items-center justify-center">
                    <i className="ri-close-line text-[11px] text-[#EF4444]"></i>
                  </div>
                  <span className="text-xs font-bold text-[#EF4444] uppercase tracking-wider">Room for Improvement</span>
                </div>
                <ul className="space-y-3">
                  {xmHonestTake.cons.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#EF4444]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[10px] text-[#EF4444]`}></i>
                      </div>
                      <p className="text-sm text-[#64748B] leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          {/* IC Markets */}
          <div className="bg-[#111827] border border-[#10B981]/20 rounded-2xl overflow-hidden">
            <div className="bg-[#10B981]/10 px-6 py-4 border-b border-[#10B981]/10 flex items-center gap-3">
              <img src="/images/ic.jpg" alt="IC Markets" className="w-8 h-8 rounded-lg object-cover" />
              <h3 className="text-lg font-bold text-[#F8FAFC]">IC Markets: The Good & The Bad</h3>
            </div>
            <div className="p-6">
              {/* Pros */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 rounded-full bg-[#10B981]/15 flex items-center justify-center">
                    <i className="ri-check-line text-[11px] text-[#10B981]"></i>
                  </div>
                  <span className="text-xs font-bold text-[#10B981] uppercase tracking-wider">Where IC Markets Shines</span>
                </div>
                <ul className="space-y-3">
                  {icmarketsHonestTake.pros.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[10px] text-[#10B981]`}></i>
                      </div>
                      <p className="text-sm text-[#94A3B8] leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
              {/* Cons */}
              <div className="pt-5 border-t border-[#1F2937]">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-5 h-5 rounded-full bg-[#EF4444]/10 flex items-center justify-center">
                    <i className="ri-close-line text-[11px] text-[#EF4444]"></i>
                  </div>
                  <span className="text-xs font-bold text-[#EF4444] uppercase tracking-wider">Room for Improvement</span>
                </div>
                <ul className="space-y-3">
                  {icmarketsHonestTake.cons.map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <div className="w-5 h-5 rounded-full bg-[#EF4444]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[10px] text-[#EF4444]`}></i>
                      </div>
                      <p className="text-sm text-[#64748B] leading-relaxed">{item.text}</p>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 5: Account Sniper Match                                    */
/* ------------------------------------------------------------------ */

function AccountMatchup() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Account Face-Off" badgeIcon="ri-crosshair-line" badgeColor="#F59E0B"
          title='Pick the Wrong Account — Lose Thousands'
          subtitle="Most beginners sign up for the default account and bleed money on wide spreads. We match XM and IC Markets flagship accounts head-to-head so you start with the right one from day one."
        />
        <div className="space-y-10">
          {accountMatchups.map((matchup) => (
            <div key={matchup.id}>
              <div className="text-center mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-[#F8FAFC] mb-2">{matchup.title}</h3>
                <p className="text-[#94A3B8] text-sm">{matchup.subtitle}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-[#120726]/80 border border-[#7C3AED]/20 rounded-2xl p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <img src="/images/xm.jpg" alt="XM" className="w-10 h-10 rounded-lg object-cover" />
                    <div>
                      <h4 className="text-lg font-bold text-[#F8FAFC]">{matchup.xmAccount.name}</h4>
                      <span className="text-[10px] text-[#8B5CF6] bg-[#7C3AED]/10 px-2 py-0.5 rounded">{matchup.xmAccount.highlight}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[{ l: "Spread", v: matchup.xmAccount.spread },{ l: "Commission", v: matchup.xmAccount.commission },{ l: "Min Deposit", v: matchup.xmAccount.minDeposit },{ l: "Leverage", v: matchup.xmAccount.leverage }].map((row) => (
                      <div key={row.l} className="bg-[#111827]/50 rounded-lg p-3">
                        <div className="text-[10px] text-[#64748B] uppercase mb-0.5">{row.l}</div>
                        <div className="text-sm font-bold text-[#F8FAFC]">{row.v}</div>
                      </div>
                    ))}
                  </div>
                  <ul className="space-y-2">
                    {matchup.xmAccount.pros.map((pro, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-[#94A3B8]">
                        <i className="ri-check-line text-[#8B5CF6] text-xs"></i>{pro}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#051C12]/80 border border-[#10B981]/20 rounded-2xl p-6 md:p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <img src="/images/ic.jpg" alt="IC Markets" className="w-10 h-10 rounded-lg object-cover" />
                    <div>
                      <h4 className="text-lg font-bold text-[#F8FAFC]">{matchup.icmarketsAccount.name}</h4>
                      <span className="text-[10px] text-[#10B981] bg-[#10B981]/10 px-2 py-0.5 rounded">{matchup.icmarketsAccount.highlight}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {[{ l: "Spread", v: matchup.icmarketsAccount.spread },{ l: "Commission", v: matchup.icmarketsAccount.commission },{ l: "Min Deposit", v: matchup.icmarketsAccount.minDeposit },{ l: "Leverage", v: matchup.icmarketsAccount.leverage }].map((row) => (
                      <div key={row.l} className="bg-[#111827]/50 rounded-lg p-3">
                        <div className="text-[10px] text-[#64748B] uppercase mb-0.5">{row.l}</div>
                        <div className="text-sm font-bold text-[#F8FAFC]">{row.v}</div>
                      </div>
                    ))}
                  </div>
                  <ul className="space-y-2">
                    {matchup.icmarketsAccount.pros.map((pro, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs text-[#94A3B8]">
                        <i className="ri-check-line text-[#10B981] text-xs"></i>{pro}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="bg-[#1A2235] border border-[#2A3345] rounded-xl p-5 md:p-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F59E0B]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="ri-lightbulb-line text-[#F59E0B]"></i>
                  </div>
                  <p className="text-sm text-[#94A3B8] leading-relaxed">{matchup.analysis}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 6: Tech & Ecosystem Combat                                 */
/* ------------------------------------------------------------------ */

function TechEcosystem() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Beyond Spreads" badgeIcon="ri-cpu-line" badgeColor="#F59E0B"
          title='Platforms, Copy Trading & Developer Tools'
          subtitle="Spreads are just the entry fee. The real difference is in platform quality, copy trading ecosystems, mobile apps, and API access. See which platform gives you the better toolkit."
        />
        <div className="space-y-6">
          {techData.map((item) => (
            <div key={item.category} className="bg-[#111827] border border-[#1F2937] rounded-2xl overflow-hidden hover:border-[#374151] transition-all">
              <div className="grid grid-cols-1 lg:grid-cols-12">
                <div className="lg:col-span-2 p-6 bg-[#1A2235] flex flex-col items-center justify-center text-center gap-2 border-b lg:border-b-0 lg:border-r border-[#2A3345]">
                  <div className="w-10 h-10 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                    <i className={`${item.icon} text-lg text-[#10B981]`}></i>
                  </div>
                  <span className="text-xs font-bold text-[#F8FAFC] leading-tight">{item.category}</span>
                </div>
                <div className="lg:col-span-5 p-6 border-b lg:border-b-0 lg:border-r border-[#2A3345]">
                  <div className="flex items-center justify-between mb-3">
                    <img src="/images/xm.jpg" alt="XM" className="w-6 h-6 rounded object-cover" />
                    <span className="text-xs font-mono font-bold text-[#8B5CF6]">{item.xm.score}/10</span>
                  </div>
                  <ScoreBar score={item.xm.score} color="#8B5CF6" />
                  <div className="text-sm font-bold text-[#F8FAFC] mt-3 mb-1.5">{item.xm.label}</div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{item.xm.detail}</p>
                </div>
                <div className="lg:col-span-5 p-6">
                  <div className="flex items-center justify-between mb-3">
                    <img src="/images/ic.jpg" alt="IC Markets" className="w-6 h-6 rounded object-cover" />
                    <span className="text-xs font-mono font-bold text-[#10B981]">{item.icmarkets.score}/10</span>
                  </div>
                  <ScoreBar score={item.icmarkets.score} color="#10B981" />
                  <div className="text-sm font-bold text-[#F8FAFC] mt-3 mb-1.5">{item.icmarkets.label}</div>
                  <p className="text-xs text-[#94A3B8] leading-relaxed">{item.icmarkets.detail}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 7: Hidden Perks                                            */
/* ------------------------------------------------------------------ */

function HiddenPerks() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#111827] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Free Money Guide" badgeIcon="ri-gift-line" badgeColor="#F59E0B"
          title='Bonuses, Pricing & VIP Treatment'
          subtitle="Free money exists — you just need to know where to look. From no-deposit bonuses to raw ECN spreads with sub-40ms execution, here's exactly what each broker will give you."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* XM */}
          <div className="bg-[#120726]/80 border border-[#7C3AED]/20 rounded-2xl overflow-hidden">
            <div className="bg-[#7C3AED]/10 px-6 py-4 border-b border-[#7C3AED]/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/images/xm.jpg" alt="XM" className="w-8 h-8 rounded-lg object-cover" />
                <h3 className="text-lg font-bold text-[#F8FAFC]">XM's Bonus Empire</h3>
              </div>
              <span className="text-[10px] text-[#8B5CF6] font-bold bg-[#7C3AED]/15 px-2 py-0.5 rounded-full">Bonus King</span>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4">
                {xmPerks.map((perk, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-[#111827]/50 rounded-xl hover:bg-[#111827] transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-[#7C3AED]/10 flex items-center justify-center shrink-0">
                      <i className={`${perk.icon} text-[#8B5CF6] text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#F8FAFC] mb-1">{perk.title}</h4>
                      <p className="text-xs text-[#94A3B8] leading-relaxed">{perk.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* IC Markets */}
          <div className="bg-[#051C12]/80 border border-[#10B981]/20 rounded-2xl overflow-hidden">
            <div className="bg-[#10B981]/10 px-6 py-4 border-b border-[#10B981]/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <img src="/images/ic.jpg" alt="IC Markets" className="w-8 h-8 rounded-lg object-cover" />
                <h3 className="text-lg font-bold text-[#F8FAFC]">IC Markets' Pure Speed</h3>
              </div>
              <span className="text-[10px] text-[#10B981] font-bold bg-[#10B981]/15 px-2 py-0.5 rounded-full">Pure ECN</span>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 gap-4">
                {icmarketsPerks.map((perk, i) => (
                  <div key={i} className="flex gap-4 p-4 bg-[#111827]/50 rounded-xl hover:bg-[#111827] transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-[#10B981]/10 flex items-center justify-center shrink-0">
                      <i className={`${perk.icon} text-[#10B981] text-lg`}></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#F8FAFC] mb-1">{perk.title}</h4>
                      <p className="text-xs text-[#94A3B8] leading-relaxed">{perk.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Module 8: Black Swan Defense                                      */
/* ------------------------------------------------------------------ */

function BlackSwanDefense() {
  const { ref, isVisible } = useScrollReveal(0.1);
  return (
    <section ref={ref} className={`py-20 md:py-28 bg-[#0B0F19] ${getRevealClasses(isVisible, "up")}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <SectionHeader
          badge="Crash Test" badgeIcon="ri-thunderstorms-line" badgeColor="#EF4444"
          title='When Markets Go Crazy — Who Protects Your Money?'
          subtitle="NFP Friday. FOMC minutes. Flash crashes. This is when shady brokers widen spreads or reject orders. Here's how XM and IC Markets actually handled real-world chaos."
        />
        <div className="bg-[#111827] border border-[#1F2937] rounded-2xl overflow-hidden">
          <div className="grid grid-cols-12 bg-[#1A2235] border-b border-[#2A3345]">
            <div className="col-span-3 p-4 text-xs font-bold text-[#64748B] uppercase tracking-wider">Metric</div>
            <div className="col-span-4 p-4 text-xs font-bold text-[#8B5CF6] uppercase tracking-wider border-l border-[#2A3345]">XM Defense</div>
            <div className="col-span-4 p-4 text-xs font-bold text-[#10B981] uppercase tracking-wider border-l border-[#2A3345]">IC Markets Defense</div>
            <div className="col-span-1 p-4 text-xs font-bold text-[#64748B] uppercase tracking-wider border-l border-[#2A3345] text-center">Winner</div>
          </div>
          {blackSwanData.map((item, i) => (
            <div key={i} className={`grid grid-cols-12 border-b border-[#1F2937] last:border-b-0 ${i % 2 === 0 ? "bg-[#111827]" : "bg-[#1A2235]/50"}`}>
              <div className="col-span-3 p-5 flex items-center">
                <span className="text-sm font-bold text-[#F8FAFC]">{item.category}</span>
              </div>
              <div className={`col-span-4 p-5 border-l border-[#1F2937] ${item.xmAdvantage ? "bg-[#7C3AED]/3" : ""}`}>
                <p className="text-xs text-[#94A3B8] leading-relaxed">{item.xm}</p>
              </div>
              <div className={`col-span-4 p-5 border-l border-[#1F2937] ${!item.xmAdvantage ? "bg-[#10B981]/3" : ""}`}>
                <p className="text-xs text-[#94A3B8] leading-relaxed">{item.icmarkets}</p>
              </div>
              <div className="col-span-1 p-5 border-l border-[#1F2937] flex items-center justify-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${item.xmAdvantage ? "bg-[#7C3AED]/15" : "bg-[#10B981]/15"}`}>
                  <img src={item.xmAdvantage ? "/images/xm.jpg" : "/images/ic.jpg"} alt="" className="w-5 h-5 rounded object-cover" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ */
/*  Full Page                                                          */
/* ------------------------------------------------------------------ */

export default function CompareIcMarkets() {
  useSEO({
    /* @replace:xm-icmarkets-title */

    title: 'XM vs IC Markets 2026 | Trading Costs, Accounts & Platform Analysis',
    /* @replace:xm-icmarkets-description */

    description: 'Review the key differences between XM and IC Markets, including account structures, trading costs, execution models, platforms, and overall trading conditions.',
    /* @replace:xm-icmarkets-keywords */

    keywords: 'XM vs IC Markets, IC Markets XM comparison, XM IC Markets review, IC Markets trading conditions, forex broker account comparison',
    /* @replace:xm-icmarkets-canonical */

    canonical: '/compare/xm-vs-icmarkets',
  });

  return (
    <div className="min-h-screen bg-[#111827]">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        "name": "XM vs IC Markets In-Depth Comparison 2026 — 1-Second Verdict + 4 Scenario Showdown + Smart Broker Quiz",
        "description": "Complete XM vs IC Markets comparison: real trading scenario showdowns, visualized parameter indicators, honest downsides, account sniper matchups, tech ecosystem battle, hidden perks, black swan stress test, and interactive broker matching quiz.",
        "keywords": "XM vs IC Markets,forex broker comparison,IC Markets review,XM review,trading platform comparison,best forex broker,ECN broker",
      }) }} />

      <HeroVerdict />
      <ScenarioShowdown />
      <AccountTypesComparison />
      <HonestDownsides />
      <AccountMatchup />
      <TechEcosystem />
      <HiddenPerks />
      <BlackSwanDefense />
      <BrokerQuiz />

      {/* Bottom CTA */}
      <section className="py-16 md:py-20 bg-[#0B0F19]">
        <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-bold text-[#F8FAFC] mb-4">
            So Which One Is It?
          </h2>
          <p className="text-[#94A3B8] text-sm md:text-base mb-8 max-w-xl mx-auto">
            You've seen the data. You know the trade-offs. XM for bonuses and low entry. IC Markets for raw ECN speed and platform diversity. Now pick your side.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={brokers.xm.href} target="_blank" rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white text-sm font-bold rounded-lg transition-all hover:shadow-lg hover:shadow-[#7C3AED]/25 whitespace-nowrap">
              Open XM Account <i className="ri-arrow-right-up-line"></i>
            </a>
            <a href={brokers.icmarkets.href} target="_blank" rel="noopener noreferrer"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-[#10B981] hover:bg-[#059669] text-white text-sm font-bold rounded-lg transition-all hover:shadow-lg hover:shadow-[#10B981]/25 whitespace-nowrap">
              Open IC Markets Account <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}